import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import { styled } from "@mui/material/styles";
import TableRow from "@mui/material/TableRow";
import { useEffect } from "react";
import { useState } from "react";
import { formatDate, showErrorToast } from "../../../../../common/components/Helper/Utils";
import DatePicker, { DateObject } from "react-multi-date-picker";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DatePanel from "react-multi-date-picker/plugins/date_panel";
import { SpinnerRoundFilled } from "spinners-react";
import {
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getContactAuditLog } from "../../actions/contactApi";
import NoPreview from "../../../../../assests/images/no-preview.png";
// Order matches Contact Creation / Edit form (top → bottom)
const FIELD_ORDER = [
    "classification_type",
    "firstname",
    "lastname",
    "businessname",
    "emailid",
    "phonecountrycode",
    "phoneno",
    "othercountrycode",
    "otherphone",
    "relationtype",
    "acceptable_payment",
    "paymentterm",
    "businesscard_imagepath",
    "sales_taxid",
    "salestax_imagepath",
    "invoice_amount",
    "timestamp_notes",
    "billingaddress1",
    "billingaddress2",
    "billingcity",
    "billingcountry",
    "billingstate",
    "billingzipcode",
    "is_same_billing",
    "shippingaddress1",
    "shippingaddress2",
    "shippingcity",
    "shippingcountry",
    "shippingstate",
    "shippingzipcode",
];

const FIELD_LABELS = {
    classification_type: "Type",
    firstname: "First Name",
    lastname: "Last Name",
    businessname: "Business Name",
    emailid: "Email",
    phoneno: "Phone Number",
    phonecountrycode: "Phone Code",
    otherphone: "Other Phone",
    othercountrycode: "Other Phone Code",
    is_same_billing: "Same as Billing",
    sales_taxid: "Sales Tax ID",
    invoice_amount: "Invoice Amount",
    paymentterm: "Preferred Payment Terms",
    businesscard_imagepath: "Business Card / ID Image",
    salestax_imagepath: "Sales Tax Certificate",
    timestamp_notes: "Notes",
    relationtype: "Relationship Type",
    acceptable_payment: "Acceptable Payment Methods",
    billingaddress1: "Billing Address 1",
    billingaddress2: "Billing Address 2",
    billingcity: "Billing City",
    billingstate: "Billing State",
    billingzipcode: "Billing Zip",
    billingcountry: "Billing Country",
    shippingaddress1: "Shipping Address 1",
    shippingaddress2: "Shipping Address 2",
    shippingcity: "Shipping City",
    shippingstate: "Shipping State",
    shippingzipcode: "Shipping Zip",
    shippingcountry: "Shipping Country",
};

const SHOW_FIELDS = new Set(Object.keys(FIELD_LABELS));

// Hidden / internal fields — never shown in Contact History
const HIDDEN_FIELDS = new Set([
    "billingcountrycode",
    "shippingcountrycode",
]);

const SKIP_KEYS = new Set([
    "id",
    "userid",
    "accountid",
    "contactid",
    "createdby",
    "createdon",
    "modifiedby",
    "modifiedon",
    "updatedon",
    "timestamp",
    "source",
    "isdeleted",
    "isshopify_contact",
    "isshopify",
    "statecode",
    "images",
]);

const KEY_ALIASES = {
    phonecode: "phonecountrycode",
    otherphonecode: "othercountrycode",
    payment_terms: "paymentterm",
    paymentterms: "paymentterm",
    preferred_payment: "paymentterm",
    contact_type: "classification_type",
    contacttype: "classification_type",
    classification: "classification_type",
    sameasbilling: "is_same_billing",
    same_as_billing: "is_same_billing",
    issamebilling: "is_same_billing",
    relation_type: "relationtype",
    contact_relationtype: "relationtype",
    // Backend audit stores payment methods as payment_method
    payment_method: "acceptable_payment",
    paymentmethod: "acceptable_payment",
    acceptablepayment: "acceptable_payment",
};

const ADDRESS_NESTED_MAP = {
    address1: "address1",
    address2: "address2",
    city: "city",
    state: "state",
    zipcode: "zipcode",
    zip: "zipcode",
    country: "country",
};

const CLASSIFICATION_TYPE_LABELS = {
    walk_in: "Walk-In / Temporary Customers",
    business_client: "Business Contact",
    end_customer: "Retail Contact",
};

const formatClassificationType = (value) => {
    const code = String(value || "").trim().toLowerCase();
    if (!code) return "";
    return CLASSIFICATION_TYPE_LABELS[code] || String(value).trim();
};

const IMAGE_PATH_KEYS = new Set(["businesscard_imagepath", "salestax_imagepath"]);

const extractImageUrls = (value) => {
    if (value === null || value === undefined) return [];
    let parsed = value;
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed || trimmed === "null") return [];
        try {
            parsed = JSON.parse(trimmed);
        } catch (e) {
            return [trimmed];
        }
    }
    if (Array.isArray(parsed)) {
        return parsed
            .map((item) => String(item || "").trim())
            .filter((item) => item && item !== "null");
    }
    if (typeof parsed === "object") {
        const images = parsed.images;
        if (Array.isArray(images)) {
            return images
                .map((item) => String(item || "").trim())
                .filter((item) => item && item !== "null");
        }
        if (typeof images === "string" && images.trim()) {
            return [images.trim()];
        }
    }
    return [];
};

const getImageFileName = (url) => {
    if (!url) return "";
    const pathname = String(url).split("?")[0];
    const parts = pathname.split("/").filter(Boolean);
    const name = decodeURIComponent(parts[parts.length - 1] || "");
    return name || String(url).trim();
};

const formatImagePathDisplay = (value) => {
    const urls = extractImageUrls(value);
    if (!urls.length) return "";
    const names = urls.map(getImageFileName).filter(Boolean);
    if (!names.length) return `${urls.length} file(s)`;
    return names.join(", ");
};

const normalizeImagePathCompare = (value) =>
    extractImageUrls(value)
        .map(getImageFileName)
        .sort((a, b) => a.localeCompare(b))
        .join("|")
        .toLowerCase();

const isImagePreviewUrl = (url) =>
    /\.(jpg|jpeg|png|gif|webp|bmp)(\?|$)/i.test(String(url || ""));

const getImageUploadSummary = (key, fromRaw, toRaw) => {
    const fromUrls = extractImageUrls(fromRaw);
    const toUrls = extractImageUrls(toRaw);
    const label = FIELD_LABELS[key] || key;

    if (!fromUrls.length && toUrls.length) {
        return { text: `${label} uploaded`, action: "uploaded", fromUrls, toUrls };
    }
    if (fromUrls.length && !toUrls.length) {
        return { text: `${label} removed`, action: "removed", fromUrls, toUrls };
    }
    return { text: `${label} updated`, action: "updated", fromUrls, toUrls };
};

const parseJsonObject = (value) => {
    if (!value) return {};
    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch (e) {
            return {};
        }
    }
    return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const formatListValue = (value) => {
    if (!value) return "";
    let parsed = value;
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
            try {
                parsed = JSON.parse(trimmed);
            } catch (e) {
                return trimmed;
            }
        } else {
            return trimmed;
        }
    }
    if (Array.isArray(parsed)) {
        return parsed
            .map((item) => {
                if (item == null) return "";
                if (typeof item === "object") {
                    return (
                        item.relation_name ||
                        item.Value ||
                        item.value ||
                        item.label ||
                        item.name ||
                        item.description ||
                        item.Id ||
                        item.id ||
                        ""
                    );
                }
                return String(item);
            })
            .filter(Boolean)
            .sort((a, b) => String(a).localeCompare(String(b)))
            .join(", ");
    }
    if (typeof parsed === "object") {
        return (
            parsed.relation_name ||
            parsed.Value ||
            parsed.value ||
            parsed.label ||
            parsed.name ||
            parsed.description ||
            ""
        );
    }
    return String(parsed);
};

const normalizeCompareValue = (key, value) => {
    if (value === null || value === undefined) return "";
    if (key === "classification_type") {
        return formatClassificationType(value).toLowerCase();
    }
    if (IMAGE_PATH_KEYS.has(key)) {
        return normalizeImagePathCompare(value);
    }
    if (key === "relationtype" || key === "acceptable_payment") {
        return formatListValue(value).toLowerCase();
    }
    if (key === "is_same_billing") {
        const flag = String(value).trim().toLowerCase();
        if (["true", "yes", "1"].includes(flag)) return "yes";
        if (["false", "no", "0", ""].includes(flag)) return "no";
    }
    if (typeof value === "boolean") return value ? "yes" : "no";
    const text = String(value).trim();
    if (!text || text === "null" || text === "undefined") return "";
    if (key === "invoice_amount") {
        const amount = Number(text);
        if (!Number.isNaN(amount)) return amount.toFixed(2);
    }
    // Keep original casing so case-only edits (e.g. email) still count as changes
    return text;
};

const displayValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "";
    if (key === "classification_type") return formatClassificationType(value);
    if (IMAGE_PATH_KEYS.has(key)) return formatImagePathDisplay(value);
    if (key === "relationtype" || key === "acceptable_payment") return formatListValue(value);
    if (key === "is_same_billing") {
        const flag = String(value).trim().toLowerCase();
        if (["true", "yes", "1"].includes(flag)) return "Yes";
        if (["false", "no", "0"].includes(flag)) return "No";
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    const text = String(value).trim();
    if (!text || text === "null" || text === "undefined") return "";
    return text;
};

const setCanonicalField = (bucket, present, key, value) => {
    const canonical = KEY_ALIASES[key] || key;
    if (!canonical || SKIP_KEYS.has(canonical) || SKIP_KEYS.has(key)) return;
    if (HIDDEN_FIELDS.has(canonical)) return;
    if (!SHOW_FIELDS.has(canonical)) return;
    present.add(canonical);
    if (bucket[canonical] === undefined || bucket[canonical] === null || bucket[canonical] === "") {
        bucket[canonical] = value;
    }
};

const nestedAddressField = (prefix, nestedKey) => {
    const mapped = ADDRESS_NESTED_MAP[String(nestedKey).toLowerCase()];
    if (!mapped) return null;
    return `${prefix}${mapped}`;
};

const flattenAddressGroup = (values, present, nestedRaw, prefix) => {
    const nested = parseJsonObject(nestedRaw);
    Object.keys(nested).forEach((nestedKey) => {
        const field = nestedAddressField(prefix, nestedKey);
        if (field) setCanonicalField(values, present, field, nested[nestedKey]);
    });
};

const canonicalizeContactData = (raw) => {
    const obj = parseJsonObject(raw);
    const values = {};
    const present = new Set();

    Object.keys(obj).forEach((key) => {
        const lower = key.toLowerCase();
        if (SKIP_KEYS.has(lower) || SKIP_KEYS.has(key)) return;
        if (HIDDEN_FIELDS.has(lower) || HIDDEN_FIELDS.has(KEY_ALIASES[lower] || lower)) {
            if (!["billingdetails", "billingaddress", "billing", "shippingdetails", "shippingaddress", "shipping"].includes(lower)) {
                return;
            }
        }

        if (["billingdetails", "billingaddress", "billing"].includes(lower)) {
            flattenAddressGroup(values, present, obj[key], "billing");
            return;
        }

        if (["shippingdetails", "shippingaddress", "shipping"].includes(lower)) {
            flattenAddressGroup(values, present, obj[key], "shipping");
            return;
        }

        setCanonicalField(values, present, KEY_ALIASES[lower] || lower, obj[key]);
        if (ADDRESS_NESTED_MAP[lower]) {
            setCanonicalField(values, present, nestedAddressField("billing", lower), obj[key]);
        }
    });

    return { values, present };
};

const getChangedFields = (oldData, newData) => {
    const oldCanon = canonicalizeContactData(oldData);
    const newCanon = canonicalizeContactData(newData);
    const changes = [];

    FIELD_ORDER.forEach((key) => {
        if (!SHOW_FIELDS.has(key)) return;

            const oldPresent = oldCanon.present.has(key);
            const newPresent = newCanon.present.has(key);
            if (!oldPresent && !newPresent) return;
            // Key dropped from the new payload is a schema difference, not a user edit
            if (oldPresent && !newPresent) return;

            const fromRaw = oldCanon.values[key];
            const toRaw = newCanon.values[key];
            const fromNorm = normalizeCompareValue(key, fromRaw);
            const toNorm = normalizeCompareValue(key, toRaw);
            if (fromNorm === toNorm) return;
            // New-only empty values are not real updates
            if (!oldPresent && !toNorm) return;

        if (key === "othercountrycode") {
            const oldOtherPhone = String(oldCanon.values.otherphone || "").trim();
            const newOtherPhone = String(newCanon.values.otherphone || "").trim();
            if (!oldOtherPhone && !newOtherPhone) return;
        }

        if (key === "invoice_amount" && !fromNorm && !toNorm) return;

        if (IMAGE_PATH_KEYS.has(key)) {
            const summary = getImageUploadSummary(key, fromRaw, toRaw);
            changes.push({
                key,
                label: FIELD_LABELS[key] || key,
                isImageUpload: true,
                summaryText: summary.text,
                action: summary.action,
                fromUrls: summary.fromUrls,
                toUrls: summary.toUrls,
            });
            return;
        }

        changes.push({
                key,
                label: FIELD_LABELS[key] || key,
                from: displayValue(key, fromRaw),
                to: displayValue(key, toRaw),
            });
        });return changes;
};

const hasAuditPayload = (row) =>
    row?.old_data != null || row?.new_data != null;

const ACTION_NAME_LABELS = {
    "updated contact": "Contact Updated",
    "added new contact": "New Contact Created",
    "added contact": "New Contact Created",
    "create contact": "New Contact Created",
    "created contact": "New Contact Created",
    "marked as obsolete": "Contact marked as Obsolete",
    "mark as obsolete": "Contact marked as Obsolete",
    "obsolete contact": "Contact marked as Obsolete",
    "contact obsolete": "Contact marked as Obsolete",
    "obsolete": "Contact marked as Obsolete",
    "reactivated contact": "Contact Reactivated",
    "reactivate contact": "Contact Reactivated",
    "marked as active": "Contact Reactivated",
    "mark as active": "Contact Reactivated",
    "activated contact": "Contact Reactivated",
    "activate contact": "Contact Reactivated",
    "revert obsolete": "Contact Reactivated",
    "reverted obsolete": "Contact Reactivated",
    "restored contact": "Contact Reactivated",
    "restore contact": "Contact Reactivated",
    "revert obsolete contact": "Contact Reactivated",
};

const getActionDisplayName = (row) => {
    const raw = String(row?.action_name || row?.action || "").trim();
    if (!raw) return "-";
    const mapped = ACTION_NAME_LABELS[raw.toLowerCase()];
    return mapped || raw;
};

const getActionDotColor = (row) => {
    const action = String(row?.action_name || row?.action || "").toLowerCase();
    if (
        action.includes("revert") ||
        action.includes("reactivat") ||
        action.includes("restored contact") ||
        action.includes("restore contact") ||
        action.includes("marked as active") ||
        action.includes("mark as active") ||
        action.includes("activated contact") ||
        action.includes("activate contact")
    ) {
        return "#22C55E";
    }
    if (action.includes("obsolete")) return "#EF4444";
    if (action.includes("create") || action.includes("added")) return "#3B82F6";
    return "#22C55E";
};

const AVATAR_COLORS = [
    { bg: "#DCF6EA", color: "#0F9D62" },
    { bg: "#DCEAFE", color: "#2563EB" },
    { bg: "#FDE8D7", color: "#C2680B" },
    { bg: "#EDE1FB", color: "#7C3AED" },
];

const getAvatarColor = (name) => {
    const code = String(name || "").trim().toUpperCase().charCodeAt(0) || 0;
    return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const getInitials = (name) => {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "-";
    return parts.length === 1
        ? parts[0].slice(0, 2).toUpperCase()
        : `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

// Split in form order: first half left column, second half right column
const splitChangedFieldsForColumns = (fields) => {
    const splitAt = Math.ceil(fields.length / 2);
    return [fields.slice(0, splitAt), fields.slice(splitAt)];
};

function ContactHistoryPdfPreview({ open, handleClose, url, title }) {
    const iframeSrc = String(url || "").startsWith("blob:")
        ? url
        : `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 8,
                }}
            >
                <span style={{ fontSize: 16, fontWeight: 600 }}>{title || "PDF Preview"}</span>
                <IconButton aria-label="close preview" onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ height: "75vh", paddingTop: 0 }}>
                <iframe
                    src={iframeSrc}
                    title={title || "PDF Preview"}
                    width="100%"
                    height="100%"
                    style={{ border: "none", borderRadius: 6 }}
                />
            </DialogContent>
        </Dialog>
    );
}

function ContactHistoryImageThumbnail({ url }) {
    const [open, setOpen] = useState(false);
    const fileName = getImageFileName(url);
    const isPdf = /\.pdf(\?|$)/i.test(String(url || ""));

    const openPreview = () => setOpen(true);
    const closePreview = () => setOpen(false);

    if (isPdf) {
        return (
            <>
                <button
                    type="button"
                    onClick={openPreview}
                    title={fileName}
                    style={{
                        border: "none",
                        background: "none",
                        padding: 0,
                        cursor: "pointer",
                    }}
                >
                    <img
                        src={NoPreview}
                        alt={fileName}
                        style={{
                            width: 72,
                            height: 72,
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #D1D5DB",
                            display: "block",
                        }}
                    />
                </button>
                <ContactHistoryPdfPreview
                    open={open}
                    handleClose={closePreview}
                    url={url}
                    title={fileName || "PDF Preview"}
                />
            </>
        );
    }

    if (!isImagePreviewUrl(url)) {
        return (
            <span style={{ fontSize: 12, color: "#3A3A3A", wordBreak: "break-all" }}>
                {fileName}
            </span>
        );
    }

    return (
        <>
            <img
                src={url}
                alt={fileName}
                title={fileName}
                onClick={openPreview}
                style={{
                    width: 72,
                    height: 72,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: "1px solid #D1D5DB",
                    display: "block",
                    cursor: "pointer",
                }}
            />
            <Dialog open={open} onClose={closePreview} maxWidth="md" fullWidth>
                <DialogTitle
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        paddingRight: 8,
                    }}
                >
                    <span style={{ fontSize: 16, fontWeight: 600 }}>{fileName || "Image Preview"}</span>
                    <IconButton aria-label="close preview" onClick={closePreview} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent style={{ paddingTop: 0 }}>
                    <img
                        src={url}
                        alt={fileName}
                        style={{
                            width: "100%",
                            maxHeight: "75vh",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}

function ContactHistoryImagePreviews({ urls }) {
    if (!urls?.length) return null;
    return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {urls.map((url, index) => (
                <ContactHistoryImageThumbnail key={`${url}-${index}`} url={url} />
            ))}
        </div>
    );
}

function ContactHistoryImageBeforeAfter({ fromUrls = [], toUrls = [] }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginTop: 8,
            }}
        >
            <div>
                <div style={{ fontSize: 11, color: "#8A8F98", marginBottom: 4, fontWeight: 600 }}>
                    Previous
                </div>
                {fromUrls?.length ? (
                    <ContactHistoryImagePreviews urls={fromUrls} />
                ) : (
                    <span style={{ fontSize: 12, color: "#8A8F98", lineHeight: "72px" }}>—</span>
                )}
            </div>
            <div style={{ color: "#9CA3AF", fontSize: 20, lineHeight: "72px", paddingTop: 18 }}>→</div>
            <div>
                <div style={{ fontSize: 11, color: "#8A8F98", marginBottom: 4, fontWeight: 600 }}>
                    Updated
                </div>
                {toUrls?.length ? (
                    <ContactHistoryImagePreviews urls={toUrls} />
                ) : (
                    <span style={{ fontSize: 12, color: "#8A8F98", lineHeight: "72px" }}>—</span>
                )}
            </div>
        </div>
    );
}

const renderChangedFieldItem = (item) => {
    if (item.isImageUpload) {
        let imageContent = null;
        if (item.action === "updated") {
            imageContent = <ContactHistoryImageBeforeAfter fromUrls={item.fromUrls} toUrls={item.toUrls} />;
        } else if (item.action === "uploaded") {
            imageContent = (
                <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: "#8A8F98", marginBottom: 4, fontWeight: 600 }}>
                        Uploaded
                    </div>
                    <ContactHistoryImagePreviews urls={item.toUrls} />
                </div>
            );
        } else if (item.action === "removed") {
            imageContent = <ContactHistoryImageBeforeAfter fromUrls={item.fromUrls} toUrls={[]} />;
        }

        return (
            <div
                key={item.key}
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 8,
                }}
            >
                <CheckCircleIcon
                    style={{ fontSize: 16, color: "#22C55E", marginTop: 1, flexShrink: 0 }}
                />
                <div>
                    <Typography
                        variant="body2"
                        component="span"
                        style={{
                            fontSize: 13,
                            color: "#3A3A3A",
                            textTransform: "none",
                            lineHeight: 1.45,
                        }}
                    >
                        <b>{item.summaryText}</b>
                    </Typography>
                    {imageContent}
                </div>
            </div>
        );
    }

    return (
    <div
        key={item.key}
        style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
        }}
    >
        <CheckCircleIcon style={{ fontSize: 16, color: "#22C55E", marginTop: 1, flexShrink: 0 }} />
        <Typography
            variant="body2"
            component="span"
            style={{
                fontSize: 13,
                color: "#3A3A3A",
                textTransform: "none",
                lineHeight: 1.45,
            }}
        >
            {item.label} changed from <b>{item.from || "–"}</b> to <b>{item.to || "–"}</b>
        </Typography>
    </div>
);
};

// Status-only events: no View/Hide Details
const isStatusOnlyAction = (row) => {
    const action = String(row?.action_name || row?.action || "").toLowerCase();
    return (
        action.includes("obsolete") ||
        action.includes("reactivat") ||
        action.includes("restored contact") ||
        action.includes("restore contact") ||
        action.includes("marked as active") ||
        action.includes("mark as active") ||
        action.includes("activated contact") ||
        action.includes("activate contact")
    );
};

const isCreateAction = (row) => {
    const actionType = String(row?.action_type || "").toUpperCase();
    if (actionType === "INSERT") return true;
    const action = String(row?.action_name || row?.action || "").toLowerCase();
    return (
        action.includes("added new contact") ||
        action.includes("added contact") ||
        action.includes("create contact") ||
        action.includes("created contact") ||
        action.includes("new contact created")
    );
};

const columns = [
    { id: "team", label: "Performed By", minWidth: 180 },
    { id: "date", label: "Date & Time", minWidth: 160 },
    { id: "event", label: "Activity", minWidth: 180 },
    { id: "details", label: "Details", minWidth: 120 },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#0F172A",
        color: theme.palette.common.white,
        fontWeight: "normal",
        fontSize: "0.875rem",
        borderBottom: "none",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textTransform: "none !important",
    },
}));

const getDefaultDateRange = () => {
    const to = new DateObject();
    const from = new DateObject().subtract(1, "years");
    return [from, to];
};

function CustomInput({ onFocus, value, onChange }) {
    return (
        <TextField
            id="outlined-basic"
            label="Activity Date"
            variant="outlined"
            onClick={onFocus}
            onFocus={onFocus}
            style={{ width: "100%" }}
            placeholder="from YYYY/MM/DD to YYYY/MM/DD"
            onPaste={(event) => event.preventDefault()}
            value={value}
            onChange={onChange}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <CalendarMonthIcon />
                    </InputAdornment>
                ),
                readOnly: true,
            }}
            inputProps={{ autoComplete: "off" }}
        />
    );
}

export default function ContactHistoryModal({ open, handleClose, data }) {
    const [rows, setRows] = useState([]);
    const [dateValue, setDateValue] = useState(getDefaultDateRange);
    const [loader, setLoader] = useState(false);
    const [key, setKey] = useState(0);
    const [expandedId, setExpandedId] = useState(null);

    const personName =
        (data?.firstname || data?.lastname
            ? `${data?.firstname || ""} ${data?.lastname || ""}`.trim()
            : "") || "";
    const businessName = (data?.businessname || "").trim();
    const nameLine = [businessName, personName].filter(Boolean).join(" / ");

    const contactPhone = data?.phoneno
        ? `${data?.phonecode ? `(${data.phonecode}) ` : ""}${data.phoneno}`
        : "";
    const contactEmail = (data?.emailid || "").trim();

    const getHistoryList = (range = dateValue) => {
        if (!open || !data?.contactid) return;

        setLoader(true);
        const payload = {
            accountid: window.sessionStorage.getItem("accountId"),
            contactid: data.contactid,
        };

        if (Array.isArray(range) && range.length === 2 && range[0] && range[1]) {
            payload.fromDate = range[0].format("YYYY-MM-DD");
            payload.toDate = range[1].format("YYYY-MM-DD");
        }

        getContactAuditLog(payload, (error, response) => {
            if (error) {
                showErrorToast(error);
                setRows([]);
                setLoader(false);
                return;
            }
            if (response && response.status === 1) {
                setRows(Array.isArray(response.data) ? response.data : []);
                setExpandedId(null);
            } else {
                showErrorToast(response?.message ?? "Something went wrong");
                setRows([]);
            }
            setLoader(false);
        });
    };

    useEffect(() => {
        if (!open || !data?.contactid) return;
        if (Array.isArray(dateValue) && dateValue.length === 1) return;
        getHistoryList(dateValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, data?.contactid, dateValue]);

    useEffect(() => {
        if (Array.isArray(dateValue) && dateValue.length === 1) return;
        setKey((prevKey) => prevKey + 1);
    }, [dateValue]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={"lg"}
            fullWidth
            disableEnforceFocus
            PaperProps={{
                style: {
                    minHeight: "450px",
                    textTransform: "none",
                },
                className: "contact-history-dialog",
            }}
        >
            <DialogTitle id="contact-history-dialog-title" style={{ textTransform: "none" }}>
                Contact History
                <IconButton
                    onClick={handleClose}
                    style={{ float: "right" }}
                    className="noprint"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ textTransform: "none" }}>
                <Grid container spacing={3} style={{ paddingTop: 8, paddingBottom: 16 }} alignItems="flex-start">
                    <Grid item xs={12} md={7}>
                        {nameLine ? (
                            <Typography
                                variant="subtitle1"
                                style={{ color: "#1A1A2E", fontWeight: 700, fontSize: 16 }}
                            >
                                {nameLine}
                            </Typography>
                        ) : null}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: 16,
                                marginTop: 6,
                            }}
                        >
                            {contactEmail ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <MailOutlineIcon style={{ fontSize: 16, color: "#9AA0A6" }} />
                                    <Typography
                                        variant="body2"
                                        style={{ color: "#5F6368", fontSize: 13 }}
                                    >
                                        {contactEmail}
                                    </Typography>
                                </div>
                            ) : null}
                            {contactPhone ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <LocalPhoneOutlinedIcon style={{ fontSize: 16, color: "#9AA0A6" }} />
                                    <Typography
                                        variant="body2"
                                        style={{ color: "#5F6368", fontSize: 13 }}
                                    >
                                        {contactPhone}
                                    </Typography>
                                </div>
                            ) : null}
                        </div>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={5}
                        style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                        <div style={{ width: "100%", maxWidth: 360 }}>
                            <DatePicker
                                key={key}
                                range
                                render={<CustomInput />}
                                value={dateValue}
                                containerStyle={{ display: "block", width: "100%" }}
                                onChange={(dateObjects) => {
                                    setDateValue(dateObjects || []);
                                }}
                                rangeHover
                                dateSeparator=" to "
                                plugins={[<DatePanel />]}
                            />
                        </div>
                    </Grid>
                    {loader ? (
                        <Grid item xs={12}>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    textAlign: "center",
                                    minHeight: 200,
                                }}
                            >
                                <SpinnerRoundFilled
                                    size={51}
                                    thickness={125}
                                    speed={80}
                                    color="rgba(57, 107, 172, 1)"
                                    className="spp"
                                    enabled={loader}
                                />
                            </div>
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <Paper style={{ width: "100%", overflow: "hidden" }}>
                                <TableContainer>
                                    <Table aria-label="contact history table">
                                        <TableHead>
                                            <TableRow>
                                                {columns.map((column) => (
                                                    <StyledTableCell
                                                        key={column.id}
                                                        align={column.align}
                                                        style={{ minWidth: column.minWidth }}
                                                    >
                                                        {column.label}
                                                    </StyledTableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.length ? (
                                                rows.map((row) => {
                                                    const isExpanded = expandedId === row.id;
                                                    const changedFields = getChangedFields(
                                                        row.old_data,
                                                        row.new_data
                                                    );
                                                    const showDetailsLink =
                                                        !isStatusOnlyAction(row) &&
                                                        !isCreateAction(row) &&
                                                        (changedFields.length > 0 ||
                                                            hasAuditPayload(row));
                                                    const changedByName = row.changedbyname || row.changed_by || "-";
                                                    const avatarColor = getAvatarColor(changedByName);
                                                    return (
                                                        <React.Fragment key={row.id}>
                                                            <TableRow hover tabIndex={-1}>
                                                                <StyledTableCell>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                                        <div
                                                                            style={{
                                                                                width: 34,
                                                                                height: 34,
                                                                                borderRadius: "50%",
                                                                                background: avatarColor.bg,
                                                                                color: avatarColor.color,
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                fontWeight: 700,
                                                                                fontSize: 12,
                                                                                flexShrink: 0,
                                                                            }}
                                                                        >
                                                                            {getInitials(changedByName)}
                                                                        </div>
                                                                        <div>
                                                                            <div style={{ fontWeight: 600 }}>{changedByName}</div>
                                                                            {row.changedbyemail ? (
                                                                                <div style={{ fontSize: 12, color: "#8A8F98" }}>
                                                                                    {row.changedbyemail}
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                </StyledTableCell>
                                                                <StyledTableCell>
                                                                    {row.changed_on ? (
                                                                        <>
                                                                            <div>{formatDate(row.changed_on, "MMM DD, YYYY")}</div>
                                                                            <div style={{ fontSize: 12, color: "#8A8F98" }}>
                                                                                {formatDate(row.changed_on, "hh:mm A")}
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </StyledTableCell>
                                                                <StyledTableCell>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                                        <span
                                                                            style={{
                                                                                width: 8,
                                                                                height: 8,
                                                                                borderRadius: "50%",
                                                                                background: getActionDotColor(row),
                                                                                flexShrink: 0,
                                                                            }}
                                                                        />
                                                                        <span style={{ fontWeight: 600 }}>{getActionDisplayName(row)}</span>
                                                                    </div>
                                                                </StyledTableCell>
                                                                <StyledTableCell>
                                                                    {showDetailsLink ? (
                                                                        <Typography
                                                                            component="button"
                                                                            type="button"
                                                                            variant="body2"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                setExpandedId(isExpanded ? null : row.id);
                                                                            }}
                                                                            style={{
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                gap: 2,
                                                                                padding: 0,
                                                                                border: "none",
                                                                                background: "none",
                                                                                color: "#2563EB",
                                                                                cursor: "pointer",
                                                                                font: "inherit",
                                                                                whiteSpace: "nowrap",
                                                                            }}
                                                                        >
                                                                            {isExpanded ? "Hide Details" : "View Details"}
                                                                            {isExpanded ? (
                                                                                <KeyboardArrowUpIcon style={{ fontSize: 18 }} />
                                                                            ) : (
                                                                                <KeyboardArrowDownIcon style={{ fontSize: 18 }} />
                                                                            )}
                                                                        </Typography>
                                                                    ) : (
                                                                        <span style={{ color: "#8A8F98" }}>N/A</span>
                                                                    )}
                                                                </StyledTableCell>
                                                            </TableRow>
                                                            {isExpanded && showDetailsLink ? (
                                                                <TableRow>
                                                                    <StyledTableCell
                                                                        colSpan={4}
                                                                        style={{ background: "#F1FBF6", padding: "14px 20px" }}
                                                                    >
                                                                        {changedFields.length ? (
                                                                            (() => {
                                                                                const [leftFields, rightFields] =
                                                                                    splitChangedFieldsForColumns(changedFields);
                                                                                return (
                                                                                    <div
                                                                                        style={{
                                                                                            display: "grid",
                                                                                            gridTemplateColumns: "1fr 1fr",
                                                                                            columnGap: 32,
                                                                                            alignItems: "start",
                                                                                        }}
                                                                                    >
                                                                            <div
                                                                                style={{
                                                                                    display: "flex",
                                                                                    flexDirection: "column",
                                                                                    gap: 10,
                                                                                }}
                                                                            >
                                                                                {leftFields.map(renderChangedFieldItem)}
                                                                                        </div>
                                                                                    <div
                                                                                        style={{
                                                                                            display: "flex",
                                                                                                flexDirection: "column",
                                                                                                gap: 10,
                                                                                            }}
                                                                                        >
                                                                                            {rightFields.map(renderChangedFieldItem)}
                                                                                    </div>
                                                                            </div>
                                                                                );
                                                                            })()
                                                                        ) : (
                                                                            <Typography variant="body2" style={{ color: "#666" }}>
                                                                                No field changes
                                                                            </Typography>
                                                                        )}
                                                                    </StyledTableCell>
                                                                </TableRow>
                                                            ) : null}
                                                        </React.Fragment>
                                                    );
                                                })
                                            ) : (
                                                <TableRow>
                                                    <StyledTableCell colSpan={4} style={{ textAlign: "center" }}>
                                                        <Typography variant="h6" className="color-BCBCBC">
                                                            No history available.
                                                        </Typography>
                                                    </StyledTableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    );
}
